import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { HttpClient, HttpHandler, HttpInterceptor, HttpRequest, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AppRoutingModule } from './app-routing.module';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideStore } from '@ngrx/store';
import { reducers } from 'src/app/store/app.store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {  provideEffects } from '@ngrx/effects';
import { AuthModule } from '@auth0/auth0-angular';
import { environment as env } from "src/environments/environment";
import { AuthEffects } from './store/effects/auth.effects';
import { RoleEffects } from './store/effects/role.effects';
import { SiteEffects } from './store/effects/site.effects';
import { ConstantsEffects } from './store/effects/constants.effects';
import { ToastrModule } from 'ngx-toastr';
import { NgSelectModule } from '@ng-select/ng-select';
import { GoogleMapsModule } from '@angular/google-maps';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { SiteValidationInterceptor } from './core/interceptors/site-validation.interceptor';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpEvent } from '@angular/common/http';
import { AuthTokenInterceptor } from './core/interceptors/auth-token.interceptor';
import { catchError, map } from 'rxjs/operators';

// Custom loader that ensures translations are loaded properly
export class CustomTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient, private prefix: string = './assets/i18n/', private suffix: string = '.json') {}

  getTranslation(lang: string): Observable<any> {
    // Add timestamp to force no caching
    const timestamp = new Date().getTime();
    const url = `${this.prefix}${lang}${this.suffix}?_t=${timestamp}`;
    
    return this.http.get(url).pipe(
      catchError(error => {
        console.error(`Error loading translation file for ${lang}:`, error);
        // If we have an error with a specific language, fallback to English
        if (lang !== 'en') {
          console.log('Falling back to English translations');
          return this.getTranslation('en');
        }
        // If even English fails, return an empty object to avoid breaking the app
        return of({});
      }),
      // Add another timestamp to response to ensure it's always treated as new
      map(response => {
        return { ...response, _timestamp: new Date().getTime() };
      })
    );
  }
}

export function HttpLoaderFactory(http: HttpClient) {
  return new CustomTranslateLoader(http);
}

@Injectable()
export class TranslationsInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.includes('/assets/i18n/')) {
      // Add timestamp to URL to prevent caching issues
      const timestamp = new Date().getTime();
      const urlWithTimestamp = req.url.includes('?') 
        ? `${req.url}&_t=${timestamp}` 
        : `${req.url}?_t=${timestamp}`;
        
      // Clone request with aggressive cache-busting headers
      const modified = req.clone({
        url: urlWithTimestamp,
        setHeaders: {
          'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
          'Pragma': 'no-cache',
          'Expires': '0',
          'X-Cache-Bust': timestamp.toString()
        }
      });
      
      // Retry the request if it fails
      return next.handle(modified);
    }
    return next.handle(req);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: 'GOOGLE_MAPS_API_KEY',
      useValue: env.googleMapsApiKey
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SiteValidationInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TranslationsInterceptor,
      multi: true
    },
    // Using our custom token interceptor for improved token management
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthTokenInterceptor,
      multi: true
    },
    provideStore(reducers),
    provideEffects([AuthEffects,RoleEffects,SiteEffects,ConstantsEffects]),
    provideStoreDevtools({ maxAge: 25 }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      }),
      GoogleMapsModule,
      AuthModule.forRoot({
        ...env.auth,
        useRefreshTokensFallback: false,
        useRefreshTokens: true,
        cacheLocation: "localstorage",
        authorizationParams: {
          redirect_uri: window.location.origin,
          audience: env.audience,
          scope: 'openid profile email offline_access'
        },
        skipRedirectCallback:
          window.location.pathname === `${env.apiURL}`,
        // Disable Auth0's built-in interceptor since we're using our own
        httpInterceptor: {
          allowedList: []
        },
      }),
      NgSelectModule,
      ToastrModule.forRoot(),
      BrowserModule, AppRoutingModule
    ),
    provideAnimations()
  ]
};